import { ALERT_CLASSES, type AlertChoices, type AlertClass } from '@contract';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/page-header';
import { ProposeChange } from '@/components/propose-change';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { callAdmin } from '@/lib/api';
import { adminQuery, invalidate } from '@/lib/query';
import { useStaff } from '@/lib/staff-context';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/_app/alerts')({
  component: AlertsPage,
});

const ALERT_LABELS: Record<AlertClass, string> = {
  breaks: 'Books breaks, and payouts held or resumed',
  pool: 'The pool statement and its booking',
  issues: 'New issue reports',
  bills_float: 'The bills float running low',
  stops: 'Freezes waiting for a checker, and kill switches turned off',
  change_requests: 'Change requests waiting for me',
  kyc_reviews: 'Verifications waiting for review',
};

/**
 * The ops alerts a staff member gets: which ones, and which of those also by
 * SMS or by email to their staff address. Push to their own Sao app is
 * always on. Being on the list at all is a change request.
 */
function AlertsPage() {
  const staff = useStaff();
  const mine = useQuery(adminQuery('alerts.mine', {}));

  return (
    <>
      <PageHeader
        title="My alerts"
        description="Ops alerts reach you on your own Sao app by push and, as you choose, by SMS or email."
      />
      {mine.isError ? (
        <Alert variant="destructive">{mine.error.message}</Alert>
      ) : null}
      {mine.data && !mine.data.linked ? (
        <Alert variant="info">
          Your Sao app account is not linked yet. Ask the super admin to link it
          from the staff page.
        </Alert>
      ) : null}
      {mine.data?.linked && !mine.data.listed ? (
        <Card>
          <CardHeader>
            <CardTitle>You are not on the alert list</CardTitle>
            <CardDescription>
              Adding someone to the list is a change another person approves.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ProposeChange
              kind="alert_recipient"
              payload={{ staff_id: staff.id, add: true }}
              label="Ask to join the alert list"
              title="Add me to the ops alert list"
              description="You will get the ops alerts on your Sao app."
            />
          </CardContent>
        </Card>
      ) : null}
      {mine.data?.listed ? (
        <Choices
          key={[
            mine.data.alerts,
            mine.data.sms_alerts,
            mine.data.email_alerts,
          ].join('|')}
          choices={mine.data}
        />
      ) : null}
    </>
  );
}

function Choices({ choices }: Readonly<{ choices: AlertChoices }>) {
  const staff = useStaff();
  const [picked, setPicked] = useState(new Set(choices.alerts));
  const [bySms, setBySms] = useState(new Set(choices.sms_alerts));
  const [byEmail, setByEmail] = useState(new Set(choices.email_alerts));
  const save = useMutation({
    mutationFn: () =>
      callAdmin('alerts.set_mine', {
        alerts: ALERT_CLASSES.filter((key) => picked.has(key)),
        sms_alerts: ALERT_CLASSES.filter((key) => bySms.has(key)),
        email_alerts: ALERT_CLASSES.filter((key) => byEmail.has(key)),
      }),
    onSuccess: async () => {
      await invalidate('alerts.mine');
      toast.success('Saved.');
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>What you get</CardTitle>
        <CardDescription>
          Your choice; nobody approves it. Email goes to {choices.email}.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {ALERT_CLASSES.map((key) => (
          <div
            key={key}
            className="flex flex-col gap-2 border-b pb-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <Checkbox
              checked={picked.has(key)}
              onCheckedChange={(on) => setPicked(toggled(picked, key, on))}
            >
              {ALERT_LABELS[key]}
            </Checkbox>
            <div className="flex gap-4 pl-7 sm:pl-0">
              <Checkbox
                checked={picked.has(key) && bySms.has(key)}
                disabled={!picked.has(key)}
                onCheckedChange={(on) => setBySms(toggled(bySms, key, on))}
              >
                SMS
              </Checkbox>
              <Checkbox
                checked={picked.has(key) && byEmail.has(key)}
                disabled={!picked.has(key)}
                onCheckedChange={(on) => setByEmail(toggled(byEmail, key, on))}
              >
                Email
              </Checkbox>
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
          <ProposeChange
            kind="alert_recipient"
            payload={{ staff_id: staff.id, add: false }}
            label="Leave the alert list"
            title="Remove me from the ops alert list"
            description="You will stop getting ops alerts."
          />
        </div>
      </CardContent>
    </Card>
  );
}

function toggled(
  set: ReadonlySet<AlertClass>,
  key: AlertClass,
  on: boolean,
): Set<AlertClass> {
  const next = new Set(set);
  if (on) next.add(key);
  else next.delete(key);
  return next;
}
